import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PathService } from './path.service';
import { ProjectService } from './project.service';

/**
 * Result of a draft ↔ album-project parity check.
 *
 * - 'in-sync': the draft's stored stamp matches the project's server
 *   lastModificationDate — the snapshot (sheet count, price) can be trusted
 *   without downloading the project.
 * - 'checked': the stamps differed (or the draft had none), so the REAL
 *   .dbproject was downloaded and its sheets counted. The caller compares
 *   `realSheetCount` with the draft's value and heals on mismatch.
 * - 'unavailable': metadata or bytes could not be fetched/parsed — callers
 *   FAIL OPEN (keep the draft values) and log; never block the user on infra.
 */
export interface AlbumProjectParityResult {
  status: 'in-sync' | 'checked' | 'unavailable';
  /** The project's server lastModificationDate (the stamp to persist). */
  stamp: string | null;
  /** Authoritative sheet count (product.content.length) — 'checked' only. */
  realSheetCount: number | null;
  /** The REAL per-sheet thumbnails from the .dbproject ('' for sheets the
   *  editor never rendered) — 'checked' only. Lets healers replace the
   *  draft's stale thumbnail grid, not just the count. */
  realPageThumbnails: string[] | null;
}

/**
 * True when the project's server stamp is NEWER than a reference stamp
 * (e.g. the draft's own lastModificationDate) — the legacy-draft staleness
 * inference: a project modified after the draft's last save cannot be
 * captured by its snapshot. Both stamps are MySQL-style
 * 'YYYY-MM-DD HH:mm:ss' strings; falls back to lexicographic comparison
 * when parsing fails. Missing stamps → false (no inference).
 */
export function projectStampNewerThan(projectStamp: string | null | undefined, referenceStamp: string | null | undefined): boolean {
  if (!projectStamp || !referenceStamp) return false;
  const project = Date.parse(String(projectStamp).replace(' ', 'T'));
  const reference = Date.parse(String(referenceStamp).replace(' ', 'T'));
  if (Number.isFinite(project) && Number.isFinite(reference)) return project > reference;
  return String(projectStamp) > String(referenceStamp);
}

/**
 * Guards the parity between what a DRAFT (.order) says about its album-pages
 * project and what the project (.dbproject) actually contains.
 *
 * Root cause guarded against (Belfiore / dev_test_2 sagas): the draft's
 * `albumProjectPageCount` + thumbnails are a SNAPSHOT written only by the
 * products-form save paths. Any project save that never returns to the form —
 * the editor's 30s auto-save followed by a tab close, edits from the Projects
 * panel or Album Design, another device — leaves the draft (and therefore the
 * displayed "Nº Folhas", the miolo price, the spine and the cart's numPages)
 * pinned to a stale count. This service gives the form and the cart an
 * authoritative check: cheap stamp comparison first, real sheet count from
 * the .dbproject when the stamps disagree.
 */
@Injectable({ providedIn: 'root' })
export class AlbumProjectParityService {
  private readonly http = inject(HttpClient);
  private readonly projectService = inject(ProjectService);
  private readonly pathService = inject(PathService);

  /**
   * Verify a draft's album project against the server.
   * `knownStamp` is the project lastModificationDate the draft stored at
   * snapshot time (null for drafts saved before this guard existed — those
   * always deep-check once and gain a stamp on their next save).
   */
  async verify(projectId: number, knownStamp: string | null): Promise<AlbumProjectParityResult> {
    console.log('[AlbumProjectParity] verify start — projectId:', projectId, 'knownStamp:', knownStamp);
    let meta: any = null;
    try {
      meta = await firstValueFrom(this.projectService.getProjectMeta(projectId));
    } catch (err) {
      console.warn('[AlbumProjectParity] getProjectMeta threw:', err);
      meta = null;
    }
    if (!meta) {
      console.warn('[AlbumProjectParity] Project meta unavailable for', projectId, '— failing open');
      return { status: 'unavailable', stamp: null, realSheetCount: null, realPageThumbnails: null };
    }

    // getProjectMeta is an untyped passthrough — the backend field is the
    // RAW lastModificationDate (lastModifiedDateString is a list-mapper alias).
    const stamp = meta.lastModificationDate ? String(meta.lastModificationDate) : null;
    console.log('[AlbumProjectParity] meta ok — server stamp:', stamp, 'url:', meta.url || '(none)',
      'serverName:', meta.serverName, 'serverId:', meta.serverId, 'pathStructureId:', meta.pathStructureId);
    if (knownStamp && stamp && knownStamp === stamp) {
      console.log('[AlbumProjectParity] stamps match — in-sync, trusting draft snapshot');
      return { status: 'in-sync', stamp, realSheetCount: null, realPageThumbnails: null };
    }

    const real = await this.readRealSheets(meta);
    if (real === null) {
      console.warn('[AlbumProjectParity] deep check unavailable — failing open');
      return { status: 'unavailable', stamp, realSheetCount: null, realPageThumbnails: null };
    }
    console.log('[AlbumProjectParity] deep check — real sheet count:', real.count,
      'thumbnails:', real.thumbnails.filter(t => !!t).length, 'of', real.thumbnails.length);
    return { status: 'checked', stamp, realSheetCount: real.count, realPageThumbnails: real.thumbnails };
  }

  /** The project's current server stamp (null when unavailable). */
  async fetchStamp(projectId: number): Promise<string | null> {
    try {
      const meta: any = await firstValueFrom(this.projectService.getProjectMeta(projectId));
      return meta?.lastModificationDate ? String(meta.lastModificationDate) : null;
    } catch {
      return null;
    }
  }

  /**
   * COVER projects: is the project actually EMPTY (no elements and no
   * background photo on any face)? Detects the observed staleness a stamp
   * cannot catch — a baked draft thumbnail lying over a cover that was
   * cleared/replaced. null = could not read (fail open).
   */
  async readCoverEmptiness(meta: any): Promise<{ isEmpty: boolean } | null> {
    try {
      const url = this.pathService.getProjectUrl(meta);
      if (!url) return null;
      const busted = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
      const raw: any = await firstValueFrom(this.http.get<any>(busted));
      const content = raw?.product?.content;
      if (!Array.isArray(content)) return null;

      for (const page of content) {
        const faces = [page, ...(Array.isArray(page?.pages) ? page.pages : []), page?.leftPage, page?.rightPage]
          .filter(Boolean);
        for (const face of faces) {
          if (Array.isArray(face.elements) && face.elements.length > 0) return { isEmpty: false };
          if (face.background?.bitmapModel?.photo) return { isEmpty: false };
        }
      }
      return { isEmpty: true };
    } catch (err) {
      console.warn('[AlbumProjectParity] Cover emptiness read failed — failing open', err);
      return null;
    }
  }

  /**
   * Download the .dbproject and read its sheets. One entry of
   * `product.content` = one folha (a DoublePage spread or a SinglePage) —
   * the same count the editor reports as pageCount. Never counts the nested
   * `pages[]` (those are the two faces of one sheet). Each sheet's own
   * `thumb` (data URL the editor baked on save, '' when never rendered)
   * comes along so healers can rebuild the draft's thumbnail grid.
   */
  private async readRealSheets(meta: any): Promise<{ count: number; thumbnails: string[] } | null> {
    try {
      const url = this.pathService.getProjectUrl(meta);
      console.log('[AlbumProjectParity] downloading project bytes from:', url || '(unresolvable)');
      if (!url) return null;
      const busted = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
      const raw: any = await firstValueFrom(this.http.get<any>(busted));

      const product = raw?.product;
      console.log('[AlbumProjectParity] project bytes parsed — hasProduct:', !!product,
        'content:', Array.isArray(product?.content) ? product.content.length : '(not array)',
        'topLevelKeys:', raw && typeof raw === 'object' ? Object.keys(raw).slice(0, 12) : typeof raw);
      const candidates = [product?.content, product?.pages, product?.giftPages, raw?.content];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length > 0) {
          const thumbnails = candidate.map((page: any) =>
            typeof page?.thumb === 'string' && page.thumb.startsWith('data:') ? page.thumb : '');
          return { count: candidate.length, thumbnails };
        }
      }
      // A parsed project with genuinely no pages: report it (0 sheets is a
      // real state the caller may want to surface), but never from a shape
      // we do not recognize.
      return product && Array.isArray(product.content) ? { count: 0, thumbnails: [] } : null;
    } catch (err) {
      console.warn('[AlbumProjectParity] Failed to download/parse project bytes — failing open', err);
      return null;
    }
  }
}
